import { Stack, Text, Icon, HStack, Button, Spacer } from '@chakra-ui/react';
import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Table } from 'antd';

import SettingsLayout from '@layouts/settings.layout';
import { useSetDocumentTitle } from '@util/hooks';
import { useNylasBillableAccounts } from '@services/query-services/nylas.query-service';
import { MicrosoftIcon, GoogleIcon } from '@assets/icons';
import { downloadNylasBillableUsersCSV } from '@util/helpers/settings.helpers';

export default function NylasBillableUsersPage() {
  const [searchParams] = useSearchParams();
  const month = searchParams.get('month');
  const year = searchParams.get('year');

  const [tableParams, setTableParams] = useState({
    pagination: {
      current: 1,
      pageSize: 20
    }
  });
  const [isDownloading, setIsDownloading] = useState(false);

  const handleTableChange = (pagination) => {
    setTableParams({ pagination });
  };

  const monthName =
    month && year
      ? new Date(year, month - 1).toLocaleString('en-US', { month: 'long', year: 'numeric' })
      : '';

  useSetDocumentTitle(`Nylas Billable Users - ${monthName} - FORA`);

  const { data, isLoading } = useNylasBillableAccounts(
    {
      month,
      year,
      page: tableParams.pagination.current,
      pageSize: tableParams.pagination.pageSize
    },
    { includeTotal: true }
  );

  const billableUsers = data?.data || [];
  const total = data?.total || 0;

  const handleDownloadCSV = async () => {
    setIsDownloading(true);
    try {
      await downloadNylasBillableUsersCSV(month, year);
    } finally {
      setIsDownloading(false);
    }
  };

  const columns = [
    {
      title: 'Name',
      dataIndex: ['person', 'name'],
      key: 'name'
    },
    {
      title: 'Email',
      dataIndex: 'email_address',
      key: 'email_address'
    },
    {
      title: 'Provider',
      dataIndex: 'provider',
      key: 'provider',
      render: (provider) => {
        const iconComponent = provider === 'microsoft' ? MicrosoftIcon : GoogleIcon;
        return (
          <Icon
            as={iconComponent}
            boxSize={5}
          />
        );
      }
    }
  ];

  return (
    <SettingsLayout pageTitle={`Nylas Billable Users - ${monthName}`}>
      <Stack
        w='full'
        maxW='1200px'
        gap={6}
      >
        <HStack>
          <Text color='surface.onSurfaceVariant'>
            Users with Nylas accounts for automated bot scheduling in {monthName}.
          </Text>
          <Spacer />
          <Button
            variant='reskinSecondary'
            size='sm'
            onClick={handleDownloadCSV}
            isDisabled={isLoading || total === 0 || isDownloading}
            isLoading={isDownloading}
            loadingText='Downloading'
          >
            Download CSV
          </Button>
        </HStack>

        <Stack
          layerStyle='section-card'
          p={6}
          gap={4}
        >
          <Table
            size='small'
            rowKey='id'
            loading={isLoading}
            columns={columns}
            dataSource={billableUsers}
            pagination={{ ...tableParams.pagination, total }}
            onChange={handleTableChange}
          />
        </Stack>
      </Stack>
    </SettingsLayout>
  );
}
